import { HttpClient } from '@angular/common/http';
import { inject, Service, signal, type Signal } from '@angular/core';
import { environment } from 'environments/environment';
import { catchError, EMPTY, tap } from 'rxjs';

interface HealthResponse {
  status: string;
  version: string;
  timestamp: string;
}

/**
 * Client for the backend `/health` endpoint — mirrors the backend `HealthModule` naming.
 *
 * Responsibilities:
 * - Exposes a read-only `version` signal (`string | null`) — the `version` field from the response.
 * - Exposes a read-only `status` signal (`string | null`) — the `status` field from the response.
 * - `fetch()` calls `GET <api.rest>/health` and stores both signals on success.
 * - On any HTTP error, both signals keep their prior value — bootstrap is never blocked.
 *
 * Lives in `@aurora/modules/health`. Registered as a root singleton via `@Service()`.
 * The fetch is triggered by `provideAppInitializer` in `app.config.ts`.
 */
@Service()
export class HealthService {
  private readonly _http = inject(HttpClient);
  private readonly _version = signal<string | null>(null);
  private readonly _status = signal<string | null>(null);

  /** Read-only signal of the application version, or `null` if not yet fetched or on error. */
  readonly version: Signal<string | null> = this._version.asReadonly();

  /** Read-only signal of the backend health status (e.g. `'ok'`), or `null` if not yet fetched or on error. */
  readonly status: Signal<string | null> = this._status.asReadonly();

  /**
   * Fetches health data from `GET <api.rest>/health`.
   * Sets both `version` and `status` signals on success; keeps prior values on any HTTP error.
   * Returns a Promise suitable for use with `provideAppInitializer`.
   */
  fetch(): Promise<void> {
    const url = `${environment.api.rest}/health`;

    return new Promise<void>((resolve) => {
      this._http
        .get<HealthResponse>(url)
        .pipe(
          tap((response) => {
            this._version.set(response.version);
            this._status.set(response.status);
          }),
          catchError(() => {
            // Silent degradation: keep prior signal values, never block bootstrap.
            return EMPTY;
          }),
        )
        .subscribe({ complete: () => resolve() });
    });
  }
}
